/**
 * useServiceWorker - React hook for service worker interactions
 * Provides cache status and control functions
 */
import { useState, useEffect, useCallback } from 'react';
import { clearCache, skipWaiting } from '@/serviceWorkerRegistration';

export const useServiceWorker = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    // Monitor online/offline status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check if service worker is registered
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        setIsRegistered(true);
      });

      // Listen for service worker updates
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // New service worker took control
        window.location.reload();
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Force refresh to get new version
  const refreshApp = useCallback(() => {
    skipWaiting();
    window.location.reload();
  }, []);

  // Clear all cached data
  const clearAllCache = useCallback(async () => {
    clearCache();
    // Also clear localStorage cache timestamps
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('cache_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
    hasUpdate,
    isRegistered,
    refreshApp,
    clearAllCache,
  };
};

export default useServiceWorker;
